import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { XIcon } from 'lucide-react';
import Button from '../../../../../ui/canon/Button';
import QuestionRenderer from '../widgets/QuestionRenderer';
import WidgetContainer from '../widgets/WidgetContainer';

const EditAnswerModal = ({ 
  open, 
  onClose, 
  question, 
  currentAnswer, 
  onSave,
  saving = false 
}) => {
  const intl = useIntl();
  const [pendingAnswer, setPendingAnswer] = useState(null);

  // Reset pending answer when modal opens with a new question
  useEffect(() => {
    if (open) {
      setPendingAnswer(null);
    }
  }, [open, question?.id]);

  if (!open || !question) return null;

  const handleAnswer = (answer) => {
    setPendingAnswer(answer);
  };

  const handleSave = () => {
    if (pendingAnswer) {
      onSave(question.id, pendingAnswer);
    }
  };

  const handleCancel = () => {
    setPendingAnswer(null);
    onClose();
  };

  // Get module and question number for display
  const getQuestionContext = () => {
    // This would ideally come from the question's position in the plan
    // For now, we'll just show the section title if available
    return question.planSection?.title || 'Edit your response';
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 animate-fade-in"
        onClick={handleCancel}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-succession-bg-primary border border-succession-border rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden pointer-events-auto animate-slide-up"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-succession-border bg-succession-bg-secondary">
            <div>
              <h2 className="text-lg font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.edit_answer_title' })}</h2>
              <p className="text-xs text-succession-text-secondary mt-0.5">{getQuestionContext()}</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleCancel}
              className="p-1 h-auto"
            >
              <XIcon className="h-5 w-5 text-succession-text-secondary" />
            </Button>
          </div>

          {/* Content */}
          <div className="px-6 py-5 overflow-y-auto max-h-[60vh]">
            {/* Question Text */}
            <div className="mb-4">
              <p className="text-base font-medium text-succession-text-primary">
                {question.questionText}
                {question.required && <span className="text-red-500 ml-1">*</span>}
              </p>
              {question.helpText && (
                <p className="text-sm text-succession-text-secondary mt-1">{question.helpText}</p>
              )}
            </div>

            {/* Widget Container - passes currentAnswer so widget shows current selection */}
            <WidgetContainer>
              <QuestionRenderer
                question={question}
                onAnswer={handleAnswer}
                answer={pendingAnswer}
                disabled={saving}
                initialValue={currentAnswer}
              />
            </WidgetContainer>

            {/* Pending Answer Confirmation - only show if different from current */}
            {pendingAnswer && (
              <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-1">{intl.formatMessage({ id: 'succession.plans.edit_answer_new_selected' })}</p>
                <p className="text-sm text-green-800 dark:text-green-300">
                  {pendingAnswer.formatted || (Array.isArray(pendingAnswer.value) ? pendingAnswer.value.join(', ') : pendingAnswer.value) || JSON.stringify(pendingAnswer)}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-succession-border bg-succession-bg-secondary">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={saving}
            >
              {intl.formatMessage({ id: 'succession.plans.edit_answer_cancel' })}
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!pendingAnswer || saving}
            >
              {saving ? intl.formatMessage({ id: 'succession.plans.edit_answer_saving' }) : intl.formatMessage({ id: 'succession.plans.edit_answer_save' })}
            </Button>
          </div>
        </div>
      </div>

      {/* Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out forwards;
        }
        .animate-slide-up {
          animation: slideUp 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default EditAnswerModal;

